import { useEffect, useRef } from 'react'

import useQueryParams from '@/lib/hooks/use-query-params'

/**
 * This hook will run a handler once if a particular
 * query param has been detected. The handler runs on a useEffect,
 * and properly handles cleanup.
 * @param param - String or array of query parameter names to check
 * @param handler - Callback function to run when parameters are found
 */
export const useQueryParamOnce = (
  param: string | string[],
  handler: (p: string | (string | null)[] | null) => (() => void) | void,
  clearAfter = true,
) => {
  const { queryParams, updateQueryParams } = useQueryParams()
  const hasRunRef = useRef(false)

  useEffect(() => {
    if (!queryParams || hasRunRef.current) {
      return
    }

    let cleanup: (() => void) | void

    if (Array.isArray(param)) {
      const values = param.map((p) => queryParams.get(p))
      // Only run if at least one parameter has a value
      if (values.some((value) => value !== null)) {
        cleanup = handler(values)
        if (clearAfter) {
          updateQueryParams(
            param.reduce((acc, p) => ({ ...acc, [p]: undefined }), {}),
          )
        }
        hasRunRef.current = true
      }
    } else {
      const value = queryParams.get(param)
      if (value) {
        cleanup = handler(value)
        if (clearAfter) {
          updateQueryParams({ [param]: undefined })
        }
        hasRunRef.current = true
      }
    }

    return () => {
      if (cleanup && typeof cleanup === 'function') {
        cleanup()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryParams]) // Only depend on queryParams changes

  // Reset the ref when param or handler changes
  useEffect(() => {
    hasRunRef.current = false
  }, [param, handler])
}
